import { Box, Grid, Link, Stack, Typography } from "@mui/material";
import StatusCell from "./statusCell";
import {
  DQDashboardOrderDto,
  SectionTone,
} from "@/app/models/dqDashboard/types";

const DEFAULT_VISIBLE = 3;

const DeliverySection: React.FC<{
  title: string;
  tone: SectionTone;
  orders: DQDashboardOrderDto[];
  expanded: boolean;
  onToggle: () => void;
}> = ({ title, tone, orders, expanded, onToggle }) => {
  const visible = expanded ? orders : orders.slice(0, DEFAULT_VISIBLE);

  const headerBg =
    tone === "yesterday" ? "#afa9a0" : tone === "today" ? "#00b388" : "#003b5c";
  const headerFg = "#fff";

  return (
    <Box sx={{ mb: 2.5 }}>
      <Box
        sx={{
          bgcolor: headerBg,
          color: headerFg,
          borderRadius: 1.5,
          fontWeight: 700,
          mb: 2,
          fontSize: "16px",
          padding: "5px 10px",
        }}
      >
        <Typography
          sx={{
            fontFamily: '"Montserrat", "Verdana", sans-serif',
            fontWeight: 700,
            fontSize: "16px",
          }}
        >
          {title}
        </Typography>
      </Box>

      {orders.length === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ px: 1, fontSize: { md: "14px" } }}
        >
          {tone === "yesterday" ? "No Deliveries" : "None Scheduled"}
        </Typography>
      ) : (
        <>
          <Stack
            direction={{ xs: "row", md: "column" }}
            spacing={{ xs: 1.5, md: 1.25 }}
            sx={{
              overflowX: { xs: "auto", md: "visible" },
              flexWrap: { xs: "nowrap", md: "wrap" },
              pb: { xs: 1, md: 0 },
            }}
          >
            {visible.map((o) => (
              <Box
                key={`${o.orderId}-${o.orderNumber}`}
                sx={{
                  bgcolor: "#fff",
                  borderRadius: 2,
                  px: 1.5,
                  py: { xs: 1.25, md: 0 },
                  width: { xs: 270, md: "auto" },
                  height: "auto",
                  minHeight: { xs: 150, md: "auto" },
                  flex: { xs: "0 0 auto", md: "1 1 auto" },
                  boxShadow: { xs: 1, md: 0 },
                  border: { xs: "1px solid #e0e0e0", md: "none" },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                {/* Desktop Layout */}
                <Grid
                  container
                  columns={12}
                  spacing={{ xs: 1, md: 2 }}
                  alignItems="flex-start"
                  sx={{
                    display: { xs: "none", md: "flex" },
                    alignContent: "flex-start",
                    fontSize: { md: "14px", sm: "12px", xs: "12px" },
                  }}
                >
                  <Grid item xs={12} md={1.5}>
                    <StatusCell tone={tone} o={o} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography
                      component="span"
                      sx={{
                        fontWeight: 700,
                        fontSize: { md: "14px", sm: "12px", xs: "12px" },
                      }}
                    >
                      Order Number:{" "}
                    </Typography>
                    <Link
                      href={`${process.env.REACT_APP_WEB_URL}/OrderDetails/${o.orderId}`}
                      underline="none"
                      sx={{ color: "#54afe2" }}
                    >
                      {o.orderNumber}
                    </Link>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography
                      component="span"
                      sx={{
                        fontWeight: 700,
                        fontSize: { md: "14px", sm: "12px", xs: "12px" },
                      }}
                    >
                      Job Name:{" "}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{ fontSize: { md: "14px", sm: "12px", xs: "12px" } }}
                    >
                      {o.jobName || "—"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3.5}>
                    <Typography
                      component="span"
                      sx={{
                        fontWeight: 700,
                        fontSize: { md: "14px", sm: "12px", xs: "12px" },
                      }}
                    >
                      Location:{" "}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{ fontSize: { md: "14px", sm: "12px", xs: "12px" } }}
                    >
                      {o.address || "—"}
                    </Typography>
                  </Grid>
                </Grid>

                {/* Mobile Layout*/}
                <Box
                  sx={{
                    display: { xs: "block", md: "none" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <StatusCell tone={tone} o={o} />
                  </Box>

                  <Typography
                    sx={{
                      fontSize: { md: "14px", sm: "12px", xs: "12px" },
                      mb: 1.5,
                    }}
                  >
                    <strong>Order Number:</strong>{" "}
                    <Link
                      href={`${process.env.REACT_APP_WEB_URL}/OrderDetails/${o.orderId}`}
                      underline="none"
                      sx={{ color: "#54afe2" }}
                    >
                      {o.orderNumber}
                    </Link>
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: { md: "14px", sm: "12px", xs: "12px" },
                      mb: 1.5,
                    }}
                  >
                    <strong>Job Name:</strong> {o.jobName || "—"}
                  </Typography>

                  <Typography
                    sx={{ fontSize: { md: "14px", sm: "12px", xs: "12px" } }}
                  >
                    <strong>Location:</strong> {o.address || "—"}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>

          {orders.length > DEFAULT_VISIBLE && (
            <Box sx={{ textAlign: "center", mt: 1.5 }}>
              <Link
                component="button"
                underline="none"
                sx={{
                  fontWeight: 600,
                  color: "#54afe2",
                  fontSize: { md: "14px", sm: "12px", xs: "12px" },
                }}
                onClick={onToggle}
              >
                {expanded
                  ? "Show Less"
                  : `Show ${orders.length - DEFAULT_VISIBLE} More`}
              </Link>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default DeliverySection;
